import { createTheme, Theme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { PaletteOptions } from '@mui/material/styles';

import {
  getAutocomplete,
  getMuiAlert,
  getMuiAlertTitle,
  getMuiButton,
  getMuiCalendarOrClockPicker,
  getMuiCheckbox,
  getMuiChip,
  getMuiCssBaseline,
  getMuiFilledInput,
  getMuiFormControlLabel,
  getMuiFormHelperText,
  getMuiIconButton,
  getMuiInput,
  getMuiInputBase,
  getMuiInputLabel,
  getMuiLinearProgress,
  getMuiLink,
  getMuiMenu,
  getMuiMenuItem,
  getMuiPaper,
  getMuiPickersDay,
  getMuiRadio,
  getMuiSelect,
  getMuiSlider,
  getMuiSnackbar,
  getMuiStep,
  getMuiStepConnector,
  getMuiStepIcon,
  getMuiStepLabel,
  getMuiSvgIcon,
  getMuiSwitch,
  getMuiTab,
  getMuiTable,
  getMuiTableCell,
  getMuiTabs,
  getMuiTextField,
  getMuiTooltip,
  getMuiTypography,
  getPrivatePickersMonth,
  getPrivatePickersYear,
} from './components';
import {
  BREAKPOINTS,
  FONT_FAMILY,
  SHAPE,
  SHAPE_BLOCKRAIL,
  SPACING,
  Themes,
} from './consts';
import { darkPalette } from './darkPalette';
import { palette } from './palette';
import { lightPaletteBlockRail } from './lightPaletteBlockRail';
import { darkPaletteBlockRail } from './darkPaletteBlockRail';
import { ThemeMode } from './types';

const getPalette = (themes: Themes, mode?: ThemeMode): PaletteOptions => {
  if (mode === 'blockrail') {
    switch (themes) {
      default:
      case Themes.light:
        return lightPaletteBlockRail;
      case Themes.dark:
        return darkPaletteBlockRail;
    }
  } else {
    switch (themes) {
      default:
      case Themes.light:
        return palette;
      case Themes.dark:
        return darkPalette;
    }
  }
};

const getBaseTheme = (themes: Themes, mode?: ThemeMode): Theme =>
  createTheme({
    palette: getPalette(themes, mode),
    spacing: SPACING,
    breakpoints: {
      values: BREAKPOINTS,
    },
    typography: {
      fontFamily: FONT_FAMILY,
    },
    shape: mode === 'blockrail' ? SHAPE_BLOCKRAIL : SHAPE,
  });

const getThemeByColor = (themes: Themes, mode?: ThemeMode): Theme => {
  if (mode === 'blockrail') {
    switch (themes) {
      default:
      case Themes.light:
        return getBaseTheme(Themes.light, 'blockrail');
      case Themes.dark:
        return getBaseTheme(Themes.dark, 'blockrail');
    }
  }

  switch (themes) {
    default:
    case Themes.light:
      return getBaseTheme(Themes.light);
    case Themes.dark:
      return getBaseTheme(Themes.dark);
  }
};

const getTheme = (themes: Themes, mode?: ThemeMode): Theme => {
  const baseTheme = getThemeByColor(themes, mode);

  return createTheme(
    deepmerge(baseTheme, {
      components: {
        MuiCssBaseline: getMuiCssBaseline(baseTheme),
        MuiAlert: getMuiAlert(baseTheme),
        MuiAlertTitle: getMuiAlertTitle(baseTheme),
        MuiAutocomplete: getAutocomplete(baseTheme),
        MuiInputBase: getMuiInputBase(baseTheme),
        MuiButton: getMuiButton(baseTheme),
        MuiIconButton: getMuiIconButton(baseTheme),
        MuiTypography: getMuiTypography(baseTheme),
        MuiInput: getMuiInput(),
        MuiInputLabel: getMuiInputLabel(baseTheme),
        MuiFilledInput: getMuiFilledInput(baseTheme),
        MuiOutlinedInput: getMuiFilledInput(baseTheme),
        MuiFormHelperText: getMuiFormHelperText(baseTheme),
        MuiTextField: getMuiTextField(baseTheme),
        MuiLinearProgress: getMuiLinearProgress(baseTheme),
        MuiCheckbox: getMuiCheckbox(baseTheme),
        MuiRadio: getMuiRadio(baseTheme),
        MuiFormControlLabel: getMuiFormControlLabel(),
        MuiTabs: getMuiTabs(baseTheme),
        MuiTab: getMuiTab(baseTheme),
        MuiSelect: getMuiSelect(baseTheme),
        MuiSnackbar: getMuiSnackbar(),
        MuiSvgIcon: getMuiSvgIcon(),
        MuiPaper: getMuiPaper(baseTheme),
        MuiStep: getMuiStep(),
        MuiStepConnector: getMuiStepConnector(baseTheme),
        MuiStepIcon: getMuiStepIcon(baseTheme),
        MuiStepLabel: getMuiStepLabel(baseTheme),
        MuiTable: getMuiTable(),
        MuiTableCell: getMuiTableCell(baseTheme),
        MuiLink: getMuiLink(baseTheme),
        MuiTooltip: getMuiTooltip(baseTheme),
        MuiMenu: getMuiMenu(baseTheme),
        MuiMenuItem: getMuiMenuItem(baseTheme),
        MuiSlider: getMuiSlider(baseTheme),
        MuiSwitch: getMuiSwitch(baseTheme),
        MuiPickersDay: getMuiPickersDay(baseTheme),
        MuiCalendarOrClockPicker: getMuiCalendarOrClockPicker(baseTheme),
        MuiDateRangePickerViewDesktop: getMuiCalendarOrClockPicker(baseTheme),
        MuiChip: getMuiChip(baseTheme),
        PrivatePickersMonth: getPrivatePickersMonth(baseTheme),
        PrivatePickersYear: getPrivatePickersYear(baseTheme),
      },
    }),
  );
};

const ankrLightTheme = getTheme(Themes.light);
const ankrDarkTheme = getTheme(Themes.dark);
const blockrailLightTheme = getTheme(Themes.light, 'blockrail');
const blockrailDarkTheme = getTheme(Themes.dark, 'blockrail');

export const getMainTheme = (themes: Themes, mode?: ThemeMode): Theme => {
  const isBlockrail = mode === 'blockrail';

  switch (themes) {
    default:
    case Themes.light:
      return isBlockrail ? blockrailLightTheme : ankrLightTheme;
    case Themes.dark:
      return isBlockrail ? blockrailDarkTheme : ankrDarkTheme;
  }
};

export const mainTheme = getMainTheme(Themes.light);
